import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import TurnoverSplitField from '@/components/submission/TurnoverSplitField';
import { Switch } from '@/components/ui/switch';
import { AIRAMark } from '@/components/brand/Marks';
import { Building2, Loader2, AlertTriangle } from 'lucide-react';
import DirectorsCard from '@/components/submission/DirectorsCard';
import LocationIntelCard from '@/components/submission/LocationIntelCard';

const RISK_TOGGLES = [
  { key: 'prior_cancellation_3yr', label: 'Prior cancellation within 3 years' },
  { key: 'overseas_operations', label: 'Overseas operations' },
  { key: 'public_access', label: 'Public access to premises' },
  { key: 'director_disqualification', label: 'Director disqualification (active)' },
  { key: 'prior_insolvency_2yr', label: 'Prior insolvency within 2 years' },
  { key: 'whs_system', label: 'WHS management system in place' },
  { key: 'risk_management', label: 'Risk management program' },
];

const COVERS = [
  { id: 'tools_equipment', label: 'Tools & Equipment' },
  { id: 'management_liability', label: 'Management Liability' },
  { id: 'money_theft', label: 'Money & Theft' },
  { id: 'business_interruption', label: 'Business Interruption' },
];

export default function SubmissionForm({ entity, clientRef, draftStatus, dupInfo, form, updateForm, toggleCover, onSubmit, onCancel, submitting, onLocationRisk }) {
  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Pre-submit duplicate warning */}
      {dupInfo?.count > 0 && (
        <Card className="p-3 rounded-sm border-l-4 bg-white flex items-start gap-2" style={{ borderLeftColor: '#E8A020' }} data-testid="presubmit-duplicate-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#E8A020' }} />
          <div className="text-xs" style={{ color: '#5C6B7A' }}>
            <p className="font-semibold" style={{ color: '#B27516' }}>
              Duplicate alert — this entity already has {dupInfo.count} submission{dupInfo.count === 1 ? '' : 's'} in the last 30 days
            </p>
            {dupInfo.recent?.slice(0, 3).map(d => (
              <p key={d.submission_id} className="font-mono text-[10px]">
                {d.submission_id?.slice(0, 8)} · {d.decision || d.status} · {d.created_at ? new Date(d.created_at).toLocaleDateString('en-AU') : ''}{d.broker_name ? ` · ${d.broker_name}` : ''}
              </p>
            ))}
          </div>
        </Card>
      )}

      {/* Entity Summary */}
      <Card className="p-4 rounded-sm border-l-4 bg-white" style={{ borderLeftColor: '#00B4D8' }}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4" style={{ color: '#1A4C7C' }} />
            <span className="font-semibold text-sm" style={{ color: '#0D1B2A' }}>{entity.legal_name}</span>
          </div>
          {clientRef && (
            <span className="flex items-center gap-2">
              {draftStatus && (
                <span className="text-[10px]" data-testid="draft-status"
                  style={{ color: draftStatus === 'saving' ? '#64748B' : '#1D9E75' }}>
                  {draftStatus === 'saving' ? 'Saving draft…' : `✓ ${draftStatus}`}
                </span>
              )}
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm" data-testid="submission-ref"
                style={{ background: 'rgba(0,180,216,0.08)', color: '#0E7490' }}>Ref: {clientRef}</span>
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs" style={{ color: '#5C6B7A' }}>
          <span>{entity.abn ? `ABN: ${entity.abn}` : `NZBN: ${entity.nzbn}`}</span>
          <span>{[entity.city, entity.state].filter(Boolean).join(', ')}</span>
          <span>{entity.industry_code}</span>
          <span>{entity.entity_type}</span>
        </div>
        <button type="button" onClick={onCancel} className="text-xs mt-2 hover:underline" style={{ color: '#1A4C7C' }}>Change entity</button>
      </Card>

      {/* Business Details */}
      <Card className="p-6 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Business Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs" style={{ color: '#5C6B7A' }}>Annual Turnover ($)</Label>
            <Input data-testid="input-turnover" type="number" value={form.annual_turnover} onChange={e => updateForm('annual_turnover', e.target.value)}
              className="mt-1 rounded-sm" placeholder="e.g. 2000000" required />
          </div>
          <div>
            <Label className="text-xs" style={{ color: '#5C6B7A' }}>Employee Count</Label>
            <Input data-testid="input-employees" type="number" value={form.employee_count} onChange={e => updateForm('employee_count', e.target.value)}
              className="mt-1 rounded-sm" placeholder="e.g. 12" required />
          </div>
          <div>
            <Label className="text-xs" style={{ color: '#5C6B7A' }}>Sum Insured ($)</Label>
            <Input data-testid="input-sum-insured" type="number" value={form.sum_insured} onChange={e => updateForm('sum_insured', e.target.value)}
              className="mt-1 rounded-sm" placeholder="e.g. 1500000" required />
          </div>
          <div>
            <Label className="text-xs" style={{ color: '#5C6B7A' }}>Excess / Deductible</Label>
            <select data-testid="select-excess" value={form.excess ?? 1000} onChange={e => updateForm('excess', parseFloat(e.target.value))}
              className="mt-1 w-full px-3 py-2 border border-input rounded-sm text-sm focus:ring-2 focus:ring-[#1A4C7C] bg-white">
              <option value={500}>$500 (+6% premium)</option>
              <option value={1000}>$1,000 (standard)</option>
              <option value={2500}>$2,500 (−7% premium)</option>
              <option value={5000}>$5,000 (−14% premium)</option>
              <option value={10000}>$10,000 (−22% premium)</option>
            </select>
          </div>
        </div>
        <TurnoverSplitField value={form.turnover_by_state}
          onChange={v => updateForm('turnover_by_state', v)} homeState={entity?.state} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs" style={{ color: '#5C6B7A' }}>Construction Type</Label>
            <select data-testid="select-construction" value={form.construction_type} onChange={e => updateForm('construction_type', e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-input rounded-sm text-sm focus:ring-2 focus:ring-[#1A4C7C] bg-white">
              <option value="brick_veneer">Brick Veneer</option><option value="concrete">Concrete</option>
              <option value="steel_frame">Steel Frame</option><option value="timber_frame">Timber Frame</option>
              <option value="weatherboard">Weatherboard</option><option value="mixed">Mixed</option>
            </select>
          </div>
          <div>
            <Label className="text-xs" style={{ color: '#5C6B7A' }}>Combustible Cladding</Label>
            <select data-testid="select-cladding" value={form.combustible_cladding} onChange={e => updateForm('combustible_cladding', e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-input rounded-sm text-sm focus:ring-2 focus:ring-[#1A4C7C] bg-white">
              <option value="none">None</option><option value="mediated">Mediated</option><option value="unmediated">Unmediated</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Directors & ACN */}
      <DirectorsCard entity={entity} form={form} updateForm={updateForm} />

      {/* Location Intelligence */}
      <LocationIntelCard entity={entity} form={form} updateForm={updateForm} onRisk={onLocationRisk} />

      {/* Claims & Risk */}
      <Card className="p-6 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Claims & Risk History</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs" style={{ color: '#5C6B7A' }}>Claims in Last 3 Years</Label>
            <Input data-testid="input-claims" type="number" value={form.claims_count} onChange={e => updateForm('claims_count', e.target.value)} className="mt-1 rounded-sm" min="0" />
          </div>
          <div>
            <Label className="text-xs" style={{ color: '#5C6B7A' }}>Total Claims Value ($)</Label>
            <Input data-testid="input-claims-value" type="number" value={form.claims_total_value} onChange={e => updateForm('claims_total_value', e.target.value)} className="mt-1 rounded-sm" min="0" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {RISK_TOGGLES.map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between py-1">
              <Label className="text-xs" style={{ color: '#5C6B7A' }}>{label}</Label>
              <Switch data-testid={`switch-${key}`} checked={form[key]} onCheckedChange={v => updateForm(key, v)} />
            </div>
          ))}
        </div>
      </Card>

      {/* Cover Selection */}
      <Card className="p-6 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Cover Selection</h3>
        <p className="text-xs" style={{ color: '#5C6B7A' }}>Select additional covers. Property and Liability are included by default.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {COVERS.map(cover => (
            <button key={cover.id} type="button" data-testid={`cover-${cover.id}`} onClick={() => toggleCover(cover.id)}
              className={`p-3 rounded-sm border text-xs text-left transition-all duration-150 ${
                form.covers_selected.includes(cover.id) ? 'border-[#00B4D8] bg-[rgba(0,180,216,0.05)]' : 'border-[rgba(136,150,168,0.2)] hover:border-[#1A4C7C]'
              }`}>
              <span className={form.covers_selected.includes(cover.id) ? 'font-semibold text-[#0D1B2A]' : 'text-[#5C6B7A]'}>{cover.label}</span>
            </button>
          ))}
        </div>
      </Card>

      {/* Submit */}
      <div className="flex items-center gap-4">
        <Button data-testid="submit-button" type="submit" disabled={submitting} className="rounded-sm font-semibold px-8" style={{ background: '#1A4C7C', height: 44 }}>
          {submitting ? (
            <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /><AIRAMark size="text-sm" /> is reviewing...</span>
          ) : 'Submit for Fraime Scoring'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-sm">Cancel</Button>
      </div>
    </form>
  );
}
